import React, { useEffect, useState } from "react";
import {
  Calendar,
  Checkbox,
  Input,
  MultiSelect,
  LocationPicker,
} from "../../../../components/Shared/Forms";
import styles from "../add-site.module.css";
import useApiService from "../../../../hooks/useApiService";
import { Industry } from "../../../../models/site-setup.interface";
import { formDataType } from "../AddSite";
import { useToast } from "../../../../context/ToastContext";
import { AddressSearch } from "../../../../components/Shared/Address/AddressSearch";
import { APIProvider } from "@vis.gl/react-google-maps";

interface SiteDetailsProps {
  formData: formDataType;
  setFormData: React.Dispatch<React.SetStateAction<formDataType>>;
  handleInputChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  fieldErrors: { [key: string]: string };
  setFieldErrors: React.Dispatch<React.SetStateAction<{ [key: string]: string }>>;
}

function SiteDetails({
  formData,
  setFormData,
  handleInputChange,
  fieldErrors,
  setFieldErrors,
}: SiteDetailsProps) {
  const [industries, setIndustries] = useState<Industry[]>([]);
  const [allOption, setAllOption] = useState<Industry>();
  const [gps, setGps] = useState<string>("");
  const [address, setAddress] = useState(formData.address || "");
  const toast = useToast();

  const { loading: loadingIndustries } = useApiService<Industry[]>({
    url: "/industry",
    method: "GET",
    apiName: "core",
    onSuccess: data => {
      setIndustries(data);
      const allInd = data.find(ind => ind.name.toLowerCase() === "all");
      if (allInd) setAllOption(allInd);
    },
    onError: error => {
      toast("error", error.message || "Error fetching industries");
    },
    fetchOnMount: true,
  });

  useEffect(() => {
    if (address && gps) {
      const gpsArr = gps.split(",");
      // Clear any existing error for this field
      if (fieldErrors[address]) {
        setFieldErrors(prevErrors => {
          const newErrors = { ...prevErrors };
          delete newErrors[address];
          return newErrors;
        });
      }
      setFormData({
        ...formData,
        address: address,
        gps_latitude: parseFloat(gpsArr[0]),
        gps_longitude: parseFloat(gpsArr[1]),
      });
    }
  }, [address, gps]);

  function handleAddressChange(newAddress: string) {
    setAddress(newAddress);
    if (fieldErrors.address) {
      setFieldErrors(prevErrors => {
        const newErrors = { ...prevErrors };
        delete newErrors.address;
        return newErrors;
      });
    }
  }

  function selectAllIndustries(val: any) {
    setFormData({
      ...formData,
      industry: val,
    });
    if (fieldErrors.industry) {
      setFieldErrors(prevErrors => {
        const newErrors = { ...prevErrors };
        delete newErrors.industry;
        return newErrors;
      });
    }
  }

  function selectIndustry(item: { label: string; value: number }, fieldName: string) {
    if (fieldErrors[fieldName]) {
      setFieldErrors(prevErrors => {
        const newErrors = { ...prevErrors };
        delete newErrors[fieldName];
        return newErrors;
      });
    }

    const industries = formData?.industry || [];

    // check if industryId contains any new ids that are not in industries
    const isNewIndustry = industries.includes(item.value);
    if (isNewIndustry) {
      // remove the industry id from industries
      const updatedIndustries = industries.filter(id => item.value !== id);
      setFormData({
        ...formData,
        industry: updatedIndustries,
      });
      return;
    }
    // check if industryId is the "all" option
    if (item.label === "All") {
      setFormData({
        ...formData,
        industry: [item.value],
      });
      return;
    }

    const isAllSelected = industries.includes(allOption?.id || -1);
    if (isAllSelected) {
      // remove the "all" option from industries
      const updatedIndustries = industries.filter(id => id !== allOption?.id);
      setFormData({
        ...formData,
        industry: [...updatedIndustries, item.value],
      });
      return;
    }

    setFormData({
      ...formData,
      industry: [...industries, ...[item.value]],
    });
  }

  return (
    <div className={"my-4"}>
      <div className={styles["responsive-grid-2"]}>
        <Input
          label={"Site/Project name"}
          name={"name"}
          value={formData.name}
          onChange={handleInputChange}
          required={true}
          error={fieldErrors?.name}
        />

        <MultiSelect
          label={"Industry"}
          options={industries.map(industry => ({
            label: industry.name,
            value: industry.id,
          }))}
          loading={loadingIndustries}
          onChange={(val: any) => {
            selectIndustry(val.selectedOption as { label: string; value: number }, "industry");
          }}
          value={formData.industry}
          required={true}
          error={fieldErrors?.industry}
          onSelectAll={(val: any) => {
            selectAllIndustries(val);
          }}
        />
      </div>
      <div className={styles["responsive-grid-2"]}>
        <div className={styles["responsive-grid-2"]}>
          <Calendar
            label={"Expected start date"}
            name={"start_at"}
            value={formData.start_at}
            onChange={handleInputChange}
            error={fieldErrors?.start_at}
          />
          <div>
            <Calendar
              label={"Expected end date"}
              name={"end_at"}
              value={formData.end_at}
              onChange={handleInputChange}
              error={fieldErrors?.end_at}
            />
            <Checkbox
              checked={formData.end_at === ""}
              label={"End date not known"}
              onChange={e => {
                if (e.checked) {
                  setFormData({
                    ...formData,
                    end_at: "",
                  });
                } else if (formData.end_at === "") {
                  const oneMonthFromNow = new Date();
                  oneMonthFromNow.setMonth(oneMonthFromNow.getMonth() + 1);

                  setFormData({
                    ...formData,
                    end_at: oneMonthFromNow,
                  });
                }
              }}
            />
          </div>
        </div>

        <AddressSearch
          includeMap={true}
          error={fieldErrors?.address}
          initialValue={formData.address}
          handleChange={event => {
            if (fieldErrors["address"]) {
              setFieldErrors(prevErrors => {
                const newErrors = { ...prevErrors };
                delete newErrors["address"];
                return newErrors;
              });
            }
            setFormData({
              ...formData,
              address: event.address,
              gps_latitude: event.location.lat,
              gps_longitude: event.location.lng,
            });
          }}
        />
      </div>
      <div>
        <Checkbox
          checked={formData.recurrence_enabled || false}
          label={"Enable recurring daily task plan"}
          onChange={e => {
            setFormData({
              ...formData,
              recurrence_enabled: e.checked,
            });
          }}
          subLabel={
            "When enabled, each new daily task plan will automatically copy tasks from the previous day."
          }
        />
      </div>
    </div>
  );
}

export { SiteDetails };
